import { format } from 'date-fns'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '../../components/button'
import { Status } from '../../components/cards/statusTag'
import Icon from '../../components/icon'
import Dropdown from '../../components/inputs/dropdown'
import { SearchInput } from '../../components/inputs/searchInput'
import { TextInput } from '../../components/inputs/textInput'
import { ExportDataModal } from '../../components/modal/app_modals/exportDataModal'
import { TableComponent } from '../../components/table'
import { HeaderSubheader } from '../../components/typography/HeaderSubheader'
import { SETTLEMENTSTATUS } from '../../constants'
import { formatCurrency, getPageFromSkip, getSkipFromPage } from '../../helpers'
import { useModalControl } from '../../hooks/useModalControl'
import { useGetSingleVendorsSettlementsQuery } from '../../redux-toolkits/vendors/vendors.slice'
import {
  DateFilterQueryProps,
  QueryProps,
  StatusTone,
} from '../../redux-toolkits/shared/shared.type'
import { Vendor } from '../../redux-toolkits/vendors/vendors.types'

export const SingleVendorSettlements = ({
  id,
  profile,
  dateFilterProps,
}: {
  id: string
  profile?: Vendor
  dateFilterProps: DateFilterQueryProps
}) => {
  const [settlementQuery, setSettlementQuery] = useState<
    QueryProps & { status?: string }
  >({
    limit: 20,
    skip: 0,
    status: 'all',
  })

  const { handleModalOpen, setModalOpen, modal, modalOpen, handleModalClose } =
    useModalControl()

  const {
    data: vendorSettlements,
    isError: vendorSettlementFetchError,
    isFetching: fetchingSettlements,
  } = useGetSingleVendorsSettlementsQuery({
    ...settlementQuery,
    id: id,
    status:
      settlementQuery.status === 'all' ? undefined : settlementQuery.status,
    ...dateFilterProps,
  })

  return (
    <>
      <div className="flex flex-col gap-6">
        <HeaderSubheader title="Settlement History" />

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="!w-full md:!max-w-[400px]">
            <SearchInput
              searchFunction={(search) => {
                setSettlementQuery({
                  ...settlementQuery,
                  search: search === '' ? undefined : search,
                  skip: 0,
                })
              }}
              placeholder="Search order"
            />
          </div>

          <div className="flex flex-wrap items-center gap-4 flex-1 justify-end">
            <Dropdown
              menuClassName=""
              menuButton={
                <Button
                  label={`Filter: ${settlementQuery.status}`
                    .toLowerCase()
                    .replaceAll('_', ' ')}
                  primary={false}
                  icon="filter"
                  className="capitalize"
                />
              }
              menuItems={[
                {
                  name: 'All',
                  value: 'all',
                },
                ...SETTLEMENTSTATUS.map((status) => {
                  return {
                    name: status.name.toLowerCase().replaceAll('_', ' '),
                    value: status.name,
                  }
                }),
              ].map((filter) => ({
                ...filter,
                onClick: () => {
                  setSettlementQuery({
                    ...settlementQuery,
                    status: filter.value,
                    skip: 0,
                  })
                },
              }))}
              menuItemClassName="capitalize"
            />

            <Button
              type="button"
              label="Export"
              primary={true}
              onClick={() => {
                handleModalOpen('exportDataModal')
              }}
              className="!w-full md:!max-w-[131px]"
            />
          </div>
        </div>

        <div className="w-full overflow-x-scroll">
          <TableComponent
            name="Settlement History"
            showName={false}
            headers={[
              'Transaction Id',
              'Harmonzied Reference',
              'Amount Settled',
              'Status',
              'Date',
            ]}
            rows={
              vendorSettlements
                ? vendorSettlements.data.results.map((item) => {
                    return {
                      id: item._id,
                      content: [
                        <Link
                          to={`/settlement/${item._id}`}
                          className="
                            hover:text-black hover:underline
                            transition-all duration-500
                          "
                          key={`${item._id}-transaction-id`}
                        >
                          {item._id}
                        </Link>,
                        <Link
                          to={`/orders/${item.order.harmonizedReference}`}
                          className="
                              hover:text-black hover:underline
                              transition-all duration-500"
                          key={`${item.order._id}-product-id`}
                        >
                          #{item.order.harmonizedReference}
                        </Link>,
                        formatCurrency(item.amount),
                        <Status
                          text={item.status.toLowerCase()}
                          type={
                            (SETTLEMENTSTATUS.find(
                              (status) =>
                                status.name.toLowerCase() ===
                                item.status.toLowerCase(),
                            )?.type ?? 'warn') as StatusTone
                          }
                          key={`${item._id}-status`}
                        />,
                        `${format(item.createdAt, 'dd/MM/yyyy h:mma')}`,
                      ],
                    }
                  })
                : []
            }
            onPageChange={(page) => {
              setSettlementQuery({
                ...settlementQuery,
                skip: getSkipFromPage(page, settlementQuery.limit ?? 0),
              })
            }}
            pageLimit={settlementQuery.limit as number}
            currentPage={getPageFromSkip(
              settlementQuery.skip ?? 0,
              settlementQuery.limit ?? 0,
            )}
            loading={fetchingSettlements}
            isEmpty={
              vendorSettlementFetchError ||
              [undefined, 0].includes(vendorSettlements?.data?.results?.length)
            }
            totalDataCount={vendorSettlements?.data.count ?? 0}
          />
        </div>
      </div>

      <ExportDataModal
        open={modalOpen && modal === 'exportDataModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={() => {
          handleModalClose()
        }}
        extraValues={{
          settlementStatus: '',
          vendorId: id ?? '',
        }}
        title={`Export ${profile?.businessInformation?.businessName}'s Settlements`}
        reportType="vendor_settlements"
      >
        {(form) => {
          return (
            <Dropdown
              menuClassName=""
              menuButton={
                <TextInput
                  type="text"
                  name="settlementStatus"
                  placeholder="Settlement status"
                  title="Settlement status"
                  value={form?.values?.settlementStatus || 'All'}
                  onBlur={form.handleBlur}
                  onChange={form.handleChange}
                  errors={''}
                  hasIcon={false}
                  extra={
                    <Icon
                      id={'left-caret'}
                      width={16}
                      height={16}
                      className="-rotate-90 text-sec-black"
                    />
                  }
                />
              }
              menuItems={[
                { name: 'All', value: 'all' },
                { name: 'Completed', value: 'COMPLETED' },
                { name: 'Pending', value: 'PENDING' },
                { name: 'Failed', value: 'FAILED' },
                {
                  name: 'Pending confirmation',
                  value: 'PENDING_CONFIRMATION',
                },
              ].map((filter) => ({
                ...filter,
                onClick: () => {
                  form.setFieldValue(
                    'settlementStatus',
                    filter.value === 'all' ? undefined : filter.value,
                  )
                },
              }))}
            />
          )
        }}
      </ExportDataModal>
    </>
  )
}
